import { combineReducers } from 'redux';
import _ from 'lodash';
import { createAction } from 'redux-actions';
import { getFormValues, startSubmit, stopSubmit, blur } from 'redux-form';
import axios from 'axios';

import { EMPLOYEE_BONUS_FORM, EMPLOYEE_BONUS_CSV_FORM } from 'src/constants/FormNames';
import * as Urls from 'src/constants/EndpointUrls';
import { serializeHttpGetParams, concatParamsToUrl, redirectTo, getCustomErrors } from 'src/utils/Http';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { getItemErrors, newItemFailure, setItem, getItem } from 'src/reducers/common/itemReducer';
import {
  setList,
  getList,
  getListQueries,
  fetchListFailure,
  fetchListSuccess,
  fetchList,
  setExtras,
  getExtras,
  addQuery,
  setQueries,
  getListPageCount,
  setSelectAllPages,
  getSelectAllPages,
  getSelectAllPageValue
} from 'src/reducers/common/listReducer';
import { REDUCER_NAME as MEMO_REDUCER_NAME } from 'src/reducers/memos/memos';
import { setGlobalErrors, setGlobalSuccesses } from 'src/reducers/global';
import {
  takeBonusesSearchQueries,
  takeBonusFromSearchForm,
  FORM_NAME_BONUSES_SEARCH,
  fetchBonusOptions
} from 'src/reducers/searchForm';
import { paramsWithFile } from 'src/utils/Form';
import { scrollToTop } from 'src/utils/Utils';
import { setStatus, fetchConfirmJobStatus, REPORT_BONUS_JOB_NAME } from 'src/reducers/JobStatus';
import { listReducer, itemReducer } from '../common';

export const REDUCER_NAME = 'employeeBonus';

// 本対応はこのissueで行う
// 本対応後は削除
// https://github.com/d-o-n-u-t-s/payrollservice/issues/6034
const SET_LOADED = 'SET_LOADED/SET_LOADED';
export const setLoaded = createAction(SET_LOADED);

export const getEmployeeBonusItemErrors = state => getItemErrors(REDUCER_NAME, state);
export const getEmployeeBonusFormValues = state => getFormValues(EMPLOYEE_BONUS_FORM)(state);
export const getEmployeeBonusList = state => getList(REDUCER_NAME, state);
export const setEmployeeBonusList = state => setList(REDUCER_NAME, state);
export const clearErrors = () => dispatch => dispatch(newItemFailure(REDUCER_NAME, []));
export const setErrors = errors => dispatch => dispatch(newItemFailure(REDUCER_NAME, errors));
export const addQueryEmployeeBonus = (key, value) => addQuery(REDUCER_NAME, key, value);
export const setEmployeeBonusQueries = queries => setQueries(REDUCER_NAME, queries);
export const setEmployeeBonusItem = state => setItem(REDUCER_NAME, state);
export const getEmployeeBonusItem = state => getItem(REDUCER_NAME, state);
export const getEmployeeBonusQueries = state =>
  takeBonusesSearchQueries(getFormValues(FORM_NAME_BONUSES_SEARCH)(state));
export const getEmployeeBonusSelectAllPages = state => getSelectAllPages(REDUCER_NAME, state);

// Only get info from state
export const getDownloadBonusFbErrors = state => {
  const errors = getEmployeeBonusItemErrors(state) || [{}];
  return (errors[0] || {}).downloadBonusFbErrors || [];
};
export const getSelectingClientBonus = state => takeBonusFromSearchForm(state) || {};

// 賞与：全てチェックオン、チェックオフアクション（ただし対象者は検索してる現在のページのみ）
export const setAllBonusCheckedTo = newCheckValue => (dispatch, getState) => {
  const pageCount = getListPageCount(REDUCER_NAME, getState());
  const bonusList = getEmployeeBonusList(getState()) || [];
  const bonusListCopy = bonusList.map(item => ({ ...item, isChecked: newCheckValue }));
  dispatch(setEmployeeBonusList({ data: bonusListCopy, pageCount }));
};

// 賞与検索
export const fetchEmployeeBonus = queries => async (dispatch, getState) => {
  try {
    dispatch(blur(FORM_NAME_BONUSES_SEARCH, 'clientBonusId', _.get(queries, 'client_bonus_id'), true));
    dispatch(fetchList(REDUCER_NAME));
    const response = await axios.get(`${Urls.EMPLOYEE_BONUS_SEARCH}`, { params: queries });
    const { totalCount = 0, displayFrom = 0, displayTo = 0 } = response.data.payload;
    const payload = {
      data: response.data.payload.employeeBonus,
      pageCount: response.data.payload.totalPages,
      totalCount,
      displayFrom,
      displayTo
    };
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(setExtras(MEMO_REDUCER_NAME, response.data.payload.memos));

    if (getEmployeeBonusSelectAllPages(getState())) {
      const selectAllPageValue = getSelectAllPageValue(REDUCER_NAME, getState());
      dispatch(setAllBonusCheckedTo(selectAllPageValue));
    }
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, exception.response.data.errors.messages));
    dispatch(setExtras(REDUCER_NAME, {}));
  }
};

// 賞与個別削除
export const deleteEmployeeBonus = employeeBonusId => async dispatch => {
  try {
    const deleteUrl = concatParamsToUrl(Urls.EMPLOYEE_BONUS_DELETE, { id: employeeBonusId });
    await axios.delete(deleteUrl);

    redirectTo(Urls.EMPLOYEE_BONUS_LIST);
  } catch (e) {
    dispatch(setGlobalErrors(e.response.data.errors.messages));
  }
};

// 賞与更新
export const updateEmployeeBonus = data => async dispatch => {
  try {
    const updateUrl = concatParamsToUrl(Urls.EMPLOYEE_BONUS_UPDATE, { id: data.id });
    const { deductions, payments, ...rest } = data;
    const allowanceItems = (deductions || []).concat(payments || []);
    const params = { employeeBonus: rest, employeeBonusAllowanceItems: allowanceItems };
    dispatch(startSubmit(EMPLOYEE_BONUS_FORM));

    await axios.put(updateUrl, params);
    // redirectTo(concatParamsToUrl(Urls.EMPLOYEE_BONUS_EDIT, { id: data.id }));

    // Reload to update to the newest data
    // and keep tracking list employee bonuses to select next employee
    window.location.reload();
  } catch (e) {
    if (e.response.status === 409) {
      dispatch(setGlobalErrors(e.response.data.errors.messages));
    } else {
      scrollToTop();
      dispatch(setErrors(e.response.data.errors.messages));
      dispatch(stopSubmit(EMPLOYEE_BONUS_FORM));
    }
  }
};

// 手入力前に戻る
export const revertEmployeeBonus = employeeBonusId => async dispatch => {
  try {
    const revertUrl = concatParamsToUrl(Urls.EMPLOYEE_BONUS_REVERT, { id: employeeBonusId });
    await axios.put(revertUrl);

    // Reload to update to the newest data
    // and keep tracking list employee bonuses to select next employee
    window.location.reload();
  } catch (e) {
    if (e.response.status === 409) {
      dispatch(setGlobalErrors(e.response.data.errors.messages));
    } else {
      scrollToTop();
      dispatch(setErrors(e.response.data.errors.messages));
    }
  }
};
// 賞与対象者：現在全ての賞与がチェックオンするか？
export const getAllBonusCheckedState = state => {
  const bonusList = getEmployeeBonusList(state);
  if (!bonusList) {
    return false;
  }
  return bonusList.every(item => item.isChecked);
};
// 賞与対象者：現在チェックオンの賞与
export const getCheckedTrueBonusList = state => {
  const bonusList = getEmployeeBonusList(state) || [];
  return (bonusList.filter(item => item.isChecked) || []).map(item => item.id).join(',');
};
// 賞与対象者：現在チェックオフの賞与
export const getCheckedFalseBonusList = state => {
  const bonusList = getEmployeeBonusList(state) || [];
  return (bonusList.filter(item => !item.isChecked) || []).map(item => item.id).join(',');
};
// 賞与対象者：個別チェックオン、チェックオフアクション
export const changeBonusItemCheckedState = bonusId => (dispatch, getState) => {
  const pageCount = getListPageCount(REDUCER_NAME, getState());
  const bonusList = getEmployeeBonusList(getState()) || [];
  const bonusListCp = [...bonusList];
  const clickedBonus = bonusList.find(item => item.id === bonusId);

  if (clickedBonus) {
    const index = bonusList.findIndex(item => item.id === clickedBonus.id);
    bonusListCp[index] = { ...clickedBonus, isChecked: !clickedBonus.isChecked };
  }
  dispatch(setEmployeeBonusList({ data: bonusListCp, pageCount }));
};

export const setIsNeedSavingConfirmation = value => async (dispatch, getState) => {
  const currentState = getState();
  const currentEmployeeBonusListExtra = getExtras(REDUCER_NAME, currentState) || {};

  dispatch(setExtras(REDUCER_NAME, { ...currentEmployeeBonusListExtra, isNeedSavingConfirmation: value }));
};

// 賞与対象者保存
export const saveBonusCheckedState = () => async (dispatch, getState) => {
  try {
    const currentState = getState();
    const checkedTrueBonusIds = getCheckedTrueBonusList(currentState);
    const checkedFalseBonusIds = getCheckedFalseBonusList(currentState);
    const clientBonusId = _.get(getEmployeeBonusQueries(currentState), 'client_bonus_id');
    const isCheckAllBonusPages = getEmployeeBonusSelectAllPages(currentState);
    const selectAllPageValue = getSelectAllPageValue(REDUCER_NAME, currentState);
    const currentQueries = getEmployeeBonusQueries(currentState);
    const params = {
      checkedTrueBonusIds,
      checkedFalseBonusIds,
      clientBonusId,
      isCheckAllBonusPages,
      selectAllPageValue,
      queries: currentQueries
    };

    await axios.post(Urls.SAVE_BONUS_CHECKED_STATE, params);
    dispatch(fetchEmployeeBonus(currentQueries));
    dispatch(setGlobalSuccesses('対象者を保存しました'));
    dispatch(setIsNeedSavingConfirmation(false));
  } catch (e) {
    if (e.response.status === 409) {
      dispatch(setGlobalErrors(e.response.data.errors.messages));
    } else {
      const msgs = getCustomErrors(e) || e.message;
      dispatch(setGlobalErrors(msgs));
    }
  }
};

// 支給・控除項目の更新: CSVファイルをダウンロード
export const downloadTemplateCsv = () => async (dispatch, getState) => {
  try {
    const encoding = (getFormValues(EMPLOYEE_BONUS_CSV_FORM)(getState()) || {}).encoding;
    const queries = getListQueries(REDUCER_NAME, getState());
    const params = { ...queries, encoding };
    dispatch(clearErrors());
    redirectTo(`${Urls.DOWNLOAD_BONUS_TEMPLATE_CSV}?${serializeHttpGetParams(params)}`);
  } catch (exception) {
    dispatch(setErrors(exception.response.data.errors.messages));
  }
};
// 支給・控除項目の更新: ダウンロードしたCSVファイルをインポートする
export const importEmployeeBonusCsv = data => async (dispatch, getState) => {
  try {
    const { clientBonusId } = getListQueries(REDUCER_NAME, getState());
    const params = paramsWithFile(data, ['employee_bonus_csv']);
    params.append('client_bonus_id', clientBonusId);
    await axios.post(Urls.IMPORT_EMPLOYEE_BONUS_CSV, params);
    dispatch(clearErrors());
    dispatch(setGlobalSuccesses('賞与データをインポートしました。'));
  } catch (exception) {
    dispatch(setErrors(exception.response.data.errors.messages));
  }
};

// 賞与振込FB（銀行）出力
export const downloadBonusFb = (data, queries = {}) => async dispatch => {
  try {
    const clientBonusId = queries.client_bonus_id || queries.bonus_id;
    const params = { ...data, clientBonusId };
    redirectTo(`${Urls.DOWNLOAD_BONUS_FB_DATA}?${serializeHttpGetParams(params)}`);
  } catch (e) {
    dispatch(setErrors([{ downloadBonusFbErrors: e.response.data.errors.messages }]));
  }
};

// 賞与振込一覧表
export const getShowBonusFbDataUrl = state => {
  const clientBonusId = _.get(getEmployeeBonusQueries(state), 'client_bonus_id');
  const params = serializeHttpGetParams({ clientBonusId });
  return `${Urls.SHOW_BONUS_FB_DATA_PAGE_URL}?${params}`;
};

// 賞与振込検索
export const fetchBonusFbData = (queries = {}) => async dispatch => {
  try {
    dispatch(setList(REDUCER_NAME, { data: [], pageCount: 0 }));
    dispatch(setExtras(REDUCER_NAME, { totalTransferAmount: 0 }));
    const { sort = 'staffCode', order = 'asc' } = queries;

    const params = serializeHttpGetParams({ clientBonusId: _.get(queries, 'client_bonus_id'), sort, order });
    const fetchUrl = `${Urls.SEARCH_BONUS_FB_DATA}?${params}`;
    const response = await axios.get(fetchUrl);

    const payload = { data: response.data.payload.employeeBonuses, pageCount: 0 }; // ページは使わない
    const totalTransferAmount = response.data.payload.totalTransferAmount;
    dispatch(fetchListSuccess(REDUCER_NAME, payload));
    dispatch(setExtras(REDUCER_NAME, { totalTransferAmount }));
  } catch (exception) {
    dispatch(setGlobalErrors(exception.response.data.errors.messages));
  }
};

// 賞与振込 CSV ダウンロード
export const downloadBonusCsvFile = (clientBonusId, queries, data) => async dispatch => {
  try {
    const { encoding } = data;
    const { sort = 'staffCode', order = 'asc' } = queries;
    const params = { clientBonusId, encoding, sort, order };
    redirectTo(`${Urls.DOWNLOAD_BONUS_FB_CSV}?${serializeHttpGetParams(params)}`);
  } catch (exception) {
    dispatch(setGlobalErrors(exception.response.data.errors.messages));
  }
};

export const selectAllPages = selectAllPagesObj => async dispatch => {
  dispatch(setSelectAllPages(REDUCER_NAME, selectAllPagesObj));
};

export const updateBonusReleaseDate = (data, callbackOnSuccess) => async dispatch => {
  try {
    const url = concatParamsToUrl(Urls.CHANGE_RELEASE_DATE_CLIENT_BONUS, { id: data.clientBonusId });
    await axios.put(url, data);
    dispatch(setGlobalSuccesses('公開日を変更しました。'));
    dispatch(fetchBonusOptions());
    if (typeof callbackOnSuccess === 'function') callbackOnSuccess();
  } catch (e) {
    const msgs = _.get(e, ['response', 'data', 'errors', 'messages']) || e.message;
    dispatch(setGlobalErrors(msgs));
  }
};

// PDF一括印刷
// 一括作成で作成されたものを印刷する
export const fetchBonusReportsPdf = clientBonusId => async dispatch => {
  try {
    const url = `${Urls.BULK_PRINT_EMPLOYEE_BONUS_REPORTS}?client_bonus_id=${clientBonusId}`;
    const w = window.open(url, '', 'toolbar=0,menubar=0,location=0');
    if (_.isFunction(w.addEventListener)) {
      w.addEventListener('load', () => {
        w.print();
      });
    }
  } catch (err) {
    dispatch(setGlobalErrors('エラーが発生しました。'));
  }
};

// PDF一括ダウンロード
// 一括作成で作成されたものをダウンロードする
export const fetchBonusReportsZip = clientBonusId => async dispatch => {
  try {
    const url = `${Urls.BULK_DOWNLOAD_EMPLOYEE_BONUS_REPORTS}?client_bonus_id=${clientBonusId}`;
    redirectTo(url);
  } catch (err) {
    dispatch(setGlobalErrors('エラーが発生しました。'));
  }
};

// PDF一括作成
export const setBonusReportDownloadJob = clientBonusId => async (dispatch, getState) => {
  const params = {
    client_bonus_id: clientBonusId
  };
  try {
    const response = await axios.post(Urls.CREATE_EMPLOYEE_BONUS_REPORTS, params);
    const state = getState();
    const jobStatusHash = state.JobStatus;
    jobStatusHash[REPORT_BONUS_JOB_NAME] = response.data.payload.jobStatus;
    dispatch(setStatus(jobStatusHash));
  } catch (err) {
    dispatch(setGlobalErrors('エラーが発生しました。'));
  }
};

export const hideConfirmBonusJobMessage = jobId => async dispatch => {
  try {
    await axios.put(Urls.HIDE_JOB_MESSAGE, {
      job_id: jobId,
      job_name: 'ConfirmClientBonusJob'
    });
    dispatch(fetchConfirmJobStatus());
  } catch (e) {
    const message = _.get(e, ['response', 'data', 'errors', 'messages']);
    if (!_.isEmpty(message)) {
      dispatch(setGlobalErrors(message));
    }
  }
};

// 本対応はこのissueで行う
// 本対応後は削除
// https://github.com/d-o-n-u-t-s/payrollservice/issues/6034
export const bonusReducer = (state = { isLoaded: false }, action) => {
  switch (action.type) {
    case SET_LOADED:
      return { ...state, isLoaded: true };
    default:
      return state;
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME),
  bonusPage: bonusReducer
});
